import * as cookieConsentAPI from '../API/services/cookieConsentService';
import {
  CURRENT_POLICY_VERSION,
  CONSENT_METHOD,
  COOKIE_CATEGORY,
} from '../constants/cookieConsent';
import moment from 'moment';

const CONSENT_KEY = 'cookie-consent';

export const CookieCategories = {
  NECESSARY: 'strictlyNecessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional',
};

const getDefaultConsent = () => ({
  version: CURRENT_POLICY_VERSION,
  timestamp: null,
  categories: {
    [CookieCategories.NECESSARY]: true, // Always true
    [CookieCategories.ANALYTICS]: false,
    [CookieCategories.MARKETING]: false,
    [CookieCategories.FUNCTIONAL]: false,
  },
  syncedWithBackend: false,
});

// Guardar referencias originales de métodos de Segment
let originalSegmentMethods = null;

const updateTrackingServices = (categories) => {
  // Update Google Tag Manager consent state
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_update',
      consent: {
        ad_storage: categories[CookieCategories.MARKETING] ? 'granted' : 'denied',
        analytics_storage: categories[CookieCategories.ANALYTICS] ? 'granted' : 'denied',
        functionality_storage: categories[CookieCategories.FUNCTIONAL] ? 'granted' : 'denied',
        personalization_storage: categories[CookieCategories.MARKETING] ? 'granted' : 'denied',
        security_storage: 'granted', // Always granted for necessary cookies
      },
    });
  }

  // Update Segment Analytics consent
  if (typeof window !== 'undefined' && window.analytics) {
    if (!categories[CookieCategories.ANALYTICS]) {
      if (
        !originalSegmentMethods &&
        window.analytics.track &&
        window.analytics.page &&
        window.analytics.identify
      ) {
        originalSegmentMethods = {
          track: window.analytics.track.bind(window.analytics),
          page: window.analytics.page.bind(window.analytics),
          identify: window.analytics.identify.bind(window.analytics),
        };
      }
      window.analytics.track = () => {};
      window.analytics.page = () => {};
      window.analytics.identify = () => {};
    } else if (originalSegmentMethods) {
      window.analytics.track = originalSegmentMethods.track;
      window.analytics.page = originalSegmentMethods.page;
      window.analytics.identify = originalSegmentMethods.identify;
    }
  }
};

/**
 * Map backend consent response to local format
 */
const mapBackendToLocal = (backendConsent) => {
  if (!backendConsent) return null;

  const rawTimestamp = backendConsent.consentDate || backendConsent.createdAt;

  return {
    version: backendConsent.consentVersion || CURRENT_POLICY_VERSION,
    timestamp: rawTimestamp,
    categories: {
      [CookieCategories.NECESSARY]: backendConsent.strictlyNecessary !== false,
      [CookieCategories.ANALYTICS]: backendConsent.analytics || false,
      [CookieCategories.MARKETING]: backendConsent.marketing || false,
      [CookieCategories.FUNCTIONAL]: backendConsent.functional || false,
    },
    syncedWithBackend: true,
    backendData: backendConsent,
  };
};

/**
 * Map local consent to backend format
 */
const mapLocalToBackend = (localConsent) => {
  return {
    functional: localConsent.categories[CookieCategories.FUNCTIONAL] || false,
    analytics: localConsent.categories[CookieCategories.ANALYTICS] || false,
    marketing: localConsent.categories[CookieCategories.MARKETING] || false,
    consentVersion: localConsent.version || CURRENT_POLICY_VERSION,
    consentMethod: CONSENT_METHOD.BANNER,
    language: document.documentElement.lang || 'es',
    consentDate: localConsent.timestamp,
  };
};

/**
 * Get consent from localStorage
 */
const getLocalConsent = () => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      const consent = JSON.parse(stored);
      // Check version compatibility
      if (consent.version === CURRENT_POLICY_VERSION) {
        return consent;
      }
    }
  } catch (e) {
    console.error('Error reading local consent:', e);
  }

  return null;
};

/**
 * Save consent to localStorage
 */
const saveLocalConsent = (consent) => {
  if (typeof window === 'undefined') return false;

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    return true;
  } catch (e) {
    console.error('Error saving local consent:', e);
    return false;
  }
};

export const getConsentStatus = () => {
  if (typeof window === 'undefined') return getDefaultConsent();

  const localConsent = getLocalConsent();
  return localConsent || getDefaultConsent();
};

const uploadLocalConsentToBackend = async (localConsent) => {
  try {
    if (!localConsent || !localConsent.timestamp) {
      return null;
    }
    // Guardar en el backend (ya se verificó que no existe)
    const backendData = mapLocalToBackend(localConsent);
    await cookieConsentAPI.upsertConsent(backendData);
    return true;
  } catch (error) {
    console.warn('Error uploading local consent to backend:', error);
    return false;
  }
};

/**
 * Sincroniza el consentimiento entre localStorage y backend
 * Debe llamarse cuando el usuario inicia sesión
 *
 * Estrategia de sincronización:
 * 1. Si hay consentimiento en backend → Descargar y usar ese (pero verificar si local es más nuevo)
 * 2. Si NO hay en backend PERO SÍ hay local → Subir el local al backend
 * 3. Si no hay en ninguno → No hacer nada (mostrar banner)
 */
export const syncConsentFromBackend = async () => {
  try {
    // Solo intentar si hay sesión activa
    if (typeof window === 'undefined' || !localStorage.getItem('token')) {
      return null;
    }

    const localConsent = getLocalConsent();
    const backendConsent = await cookieConsentAPI.getCurrentConsent();

    // CASO 1: Hay consentimiento en backend
    if (backendConsent && backendConsent.consentDate) {
      const hasLocalConsent = localConsent && localConsent.timestamp;

      // Si hay consentimiento local, comparar fechas
      if (hasLocalConsent) {
        const localDate = moment(localConsent.timestamp);
        const backendDate = moment(backendConsent.consentDate);

        // Si el local es más nuevo que el backend, actualizar backend
        if (localDate.isAfter(backendDate)) {
          const backendData = mapLocalToBackend(localConsent);
          await cookieConsentAPI.upsertConsent(backendData);

          // Emitir evento de actualización
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('cookie-consent-updated'));
          }

          return localConsent;
        }
      }

      // El backend tiene el consentimiento más reciente, usarlo
      const categories = {
        [CookieCategories.NECESSARY]: true,
        [CookieCategories.ANALYTICS]: Boolean(backendConsent.analytics),
        [CookieCategories.MARKETING]: Boolean(backendConsent.marketing),
        [CookieCategories.FUNCTIONAL]: Boolean(backendConsent.functional),
      };
      const consent = {
        version: CURRENT_POLICY_VERSION,
        timestamp: backendConsent.consentDate,
        categories,
        syncedWithBackend: true,
      };

      // Guardar en localStorage
      saveLocalConsent(consent);

      // Actualizar servicios de tracking
      updateTrackingServices(categories);

      // Emitir evento de actualización
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cookie-consent-updated'));
      }

      return consent;
    }

    // CASO 2: NO hay en backend PERO SÍ hay consentimiento local → Subir al backend
    if (localConsent && localConsent.timestamp) {
      await uploadLocalConsentToBackend(localConsent);
      return localConsent;
    }

    // CASO 3: No hay consentimiento ni local ni en backend
    return null;
  } catch (error) {
    // Si es error 404, significa que no hay consentimiento en backend
    if (error.response && error.response.status === 404) {
      // Verificar si hay consentimiento local para subir
      const localConsent = getLocalConsent();
      if (localConsent && localConsent.timestamp) {
        await uploadLocalConsentToBackend(localConsent);
        return localConsent;
      }

      return null;
    }

    console.error('Error syncing consent from backend:', error);
    return null;
  }
};

/**
 * Initialize consent with priority system (MEJORADO)
 * Ahora incluye comparación de fechas para sincronizar correctamente
 *
 * @param {boolean} isAuthenticated - Si el usuario está autenticado
 * @returns {Object} El consentimiento actual o default
 */
export const initializeConsentWithSync = async (isAuthenticated = false) => {
  if (typeof window === 'undefined') return getDefaultConsent();

  // Si está autenticado, ejecutar sincronización mejorada
  if (isAuthenticated && localStorage.getItem('token')) {
    const syncedConsent = await syncConsentFromBackend();

    // Si se sincronizó correctamente, retornar ese consentimiento
    if (syncedConsent && syncedConsent.timestamp) {
      return syncedConsent;
    }
  }

  // Si no está autenticado o no se pudo sincronizar, usar localStorage
  const localConsent = getLocalConsent();
  if (localConsent && localConsent.timestamp) {
    updateTrackingServices(localConsent.categories);
    return localConsent;
  }

  // Set default consent state for Google Tag Manager
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
  }

  const defaultConsent = getDefaultConsent();
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_default',
      consent: {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      },
    });
  }

  return defaultConsent;
};

/**
 * Save consent locally and sync with backend if authenticated
 */
export const saveConsent = async (categories) => {
  const consent = {
    version: CURRENT_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      [CookieCategories.NECESSARY]: true, // Always true
      ...categories,
    },
  };

  try {
    // Guardar en localStorage primero (sincrónico)
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

    // Trigger consent update for tracking services
    updateTrackingServices(consent.categories);

    // Guardar en el backend (asincrónico) - solo si hay token de sesión
    if (typeof window !== 'undefined' && localStorage.getItem('token')) {
      try {
        // Verificar primero si ya existe consentimiento en el backend
        const existingConsent = await cookieConsentAPI.getCurrentConsent();

        if (!existingConsent) {
          // No existe en backend, crear uno nuevo
          const backendData = mapLocalToBackend(consent);
          await cookieConsentAPI.upsertConsent(backendData);
        } else {
          // Existe en backend, comparar fechas
          const existingDate = moment(existingConsent.consentDate);
          const newDate = moment(consent.timestamp);

          // Si el nuevo consentimiento es más reciente, actualizar backend
          if (newDate.isAfter(existingDate)) {
            const backendData = mapLocalToBackend(consent);
            await cookieConsentAPI.upsertConsent(backendData);
          }
        }
      } catch (backendError) {
        // Si falla el backend, no afecta la UX - ya está guardado localmente
        console.warn('Error saving consent to backend:', backendError);
      }
    }

    return consent;
  } catch (e) {
    console.error('Error saving consent:', e);
    return null;
  }
};

export const hasUserConsented = () => {
  const consent = getConsentStatus();
  return consent.timestamp !== null;
};

export const hasFullConsent = () => {
  const consent = getConsentStatus();

  // If user has full consent, banner never shows
  if (
    consent.timestamp !== null &&
    consent.categories[CookieCategories.ANALYTICS] &&
    consent.categories[CookieCategories.MARKETING] &&
    consent.categories[CookieCategories.FUNCTIONAL]
  ) {
    return true;
  }

  // If user has partial consent, check if 24 hours have passed
  if (consent.timestamp !== null) {
    const consentDate = new Date(consent.timestamp);
    const now = new Date();
    const hoursSinceConsent = (now - consentDate) / (1000 * 60 * 60);

    // If less than 24 hours have passed, treat as having full consent (hide banner)
    if (hoursSinceConsent < 24) {
      return true;
    }
  }

  // No consent or more than 24 hours with partial consent
  return false;
};

export const acceptAllCookies = () =>
  saveConsent({
    [CookieCategories.ANALYTICS]: true,
    [CookieCategories.MARKETING]: true,
    [CookieCategories.FUNCTIONAL]: true,
  });

export const rejectAllCookies = () =>
  saveConsent({
    [CookieCategories.ANALYTICS]: false,
    [CookieCategories.MARKETING]: false,
    [CookieCategories.FUNCTIONAL]: false,
  });

export const acceptSelectedCategories = (categories) => saveConsent(categories);

/**
 * Legacy initialization (for backward compatibility)
 * Use initializeConsentWithSync for new implementations
 */
export const initializeConsent = () => {
  const consent = getConsentStatus();

  // Set default consent state for Google Tag Manager
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_default',
      consent: {
        ad_storage: consent.categories[CookieCategories.MARKETING] ? 'granted' : 'denied',
        analytics_storage: consent.categories[CookieCategories.ANALYTICS] ? 'granted' : 'denied',
        functionality_storage: consent.categories[CookieCategories.FUNCTIONAL]
          ? 'granted'
          : 'denied',
        personalization_storage: consent.categories[CookieCategories.MARKETING]
          ? 'granted'
          : 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      },
    });
  }

  // Apply consent to existing services
  if (consent.timestamp) {
    updateTrackingServices(consent.categories);
  }
};
